import { ReactNode, useCallback, useContext, useEffect, createContext } from 'react';

import { noop } from 'components/utils';

import useAuth from '../../hooks/use-auth';
import { CustomEventNames, EventTypes } from './constants';
import { init } from './init';

export interface ILogCustomerDobChangedEventOptions {
  previousDob: string;
  newDob: string;
  customerId: string;
  customerEmail: string;
}

export interface ILogPersonalizedOfferAssignedEventOptions {
  email: string;
  customerId: string;
  loyaltyId: string;
  offerTemplateId: string;
  offerTemplateName: string;
  reason: string;
  comments?: string;
  orderId?: string;
}

interface IMParticleContext {
  updateUserIdentities(options: { email: string; customerId: string; callback: () => void }): void;
  logCustomerDobChangedEvent(options: ILogCustomerDobChangedEventOptions): void;
  logPersonalizedOfferAssigned(options: ILogPersonalizedOfferAssignedEventOptions): void;
}

const MParticleContext = createContext<IMParticleContext>({
  updateUserIdentities: noop,
  logCustomerDobChangedEvent: noop,
  logPersonalizedOfferAssigned: noop,
});

/**
 * Provides mParticle utilities
 */
export const useMParticleContext = () => useContext(MParticleContext);

/**
 * Provides mParticle utilities
 */
export const MParticleProvider = (props: { children: ReactNode }) => {
  const { email: userEmail, cognitoId: supportAgentId } = useAuth();

  useEffect(() => {
    init();
  }, []);

  const logCustomerDobChangedEvent = ({
    previousDob,
    newDob,
    customerId,
    customerEmail,
  }: ILogCustomerDobChangedEventOptions) => {
    const logAction = () =>
      window.mParticle.logEvent(CustomEventNames.UPDATE_USER_ATTRIBUTES, EventTypes.Other, {
        'Previous DOB': previousDob,
        'New DOB': newDob,
        'Customer ID': customerId,
        'Performed By': userEmail,
      });

    updateUserIdentities({
      customerId,
      email: customerEmail,
      callback: logAction,
    });
  };

  const updateUserIdentities = ({
    email,
    customerId,
    callback,
  }: {
    email: string;
    customerId: string;
    callback: () => void;
  }) => {
    window.mParticle.Identity.login(
      { userIdentities: { email, customerid: customerId || '' } },
      callback,
    );
  };

  /**
   * Log Personalized Offer Asignment to mParticle
   */
  const logPersonalizedOfferAssigned = useCallback(
    ({
      email,
      customerId,
      loyaltyId,
      offerTemplateId,
      offerTemplateName,
      reason,
      comments,
    }: ILogPersonalizedOfferAssignedEventOptions) => {
      const logAction = () =>
        window.mParticle.logEvent(CustomEventNames.PERSONALIZED_OFFER_ASSIGNED, EventTypes.Other, {
          'Customer Email': email,
          'Customer ID': customerId,
          'Customer Loyalty ID': loyaltyId,
          'Offer Template ID': offerTemplateId,
          'Offer Template Name': offerTemplateName,
          'Remediation Reason': reason,
          'Support Agent ID': supportAgentId,
          'Support Agent Comments': comments,
        });

      updateUserIdentities({
        email,
        customerId,
        callback: logAction,
      });
    },
    [supportAgentId],
  );

  return (
    <MParticleContext.Provider
      value={{
        updateUserIdentities,
        logCustomerDobChangedEvent,
        logPersonalizedOfferAssigned,
      }}
    >
      {props.children}
    </MParticleContext.Provider>
  );
};
